import clsx from 'clsx';
import type { ComponentPropsWithRef } from 'react';
import { useCallback, useDeferredValue, useId } from 'react';
import {
  type FieldError,
  type UseFormReturn,
  useFormContext,
} from 'react-hook-form';
import Icon from '../Icon/Icon';
import InputGroup from '../InputGroup';
import Tooltip from '../Tooltip/Tooltip';

export type InputProps = {
  name: string;
  label?: string | React.ReactNode;
  labelClassName?: string;
  labelDescription?: string | React.ReactNode;
  inputClassName?: string;
  inputContainerClassName?: string;
  tooltip?: string;
  error?: FieldError | false;
  // allow for overriding the error message with a custom one (e.g. with extra context from other form fields etc.)
  errorMessage?: (error: FieldError) => string;
  valid?: boolean;
  after?: string;
  maxLength?: number;
  hideError?: boolean;
  // we have to hardcode defaultValue type instead picking it from <input>,
  // as native input element accepts string[], and <NumericFormat> from 'react-number-format does not
  defaultValue?: string | null;
} & ComponentPropsWithRef<'input'>;

export type BaseInputProps = {
  error?: boolean;
  valid?: boolean;
  after?: string;
} & ComponentPropsWithRef<'input'>;

export const BaseInput = ({
  className,
  error,
  valid,
  after,
  ref,
  ...props
}: BaseInputProps) => {
  return (
    <div className="relative flex items-center justify-center">
      <input
        {...props}
        ref={ref}
        data-testid={props.name}
        aria-invalid={!valid}
        className={clsx('w-full', className, {
          'pr-10': after && after !== '',
          '!border-red-100 !ring-red-100': error,
        })}
      />
      {after && after !== '' ? (
        <span className="typ-ui absolute right-2.5 top-[0.54rem] text-neutral-100">
          {after}
        </span>
      ) : null}
    </div>
  );
};

export const Input = ({
  label,
  labelClassName,
  labelDescription,
  placeholder,
  inputContainerClassName,
  inputClassName,
  error,
  errorMessage,
  className,
  disabled,
  valid,
  tooltip,
  maxLength,
  onChange,
  onKeyDown,
  onPaste,
  hideError,
  type = 'text',
  defaultValue,
  ref,
  ...rest
}: InputProps) => {
  const inputId = useId();
  const errorId = `${inputId}-error`;

  const form = useFormContext() as UseFormReturn | undefined;
  const fieldValue = useDeferredValue(form ? form.watch(rest.name) : null);
  const currentLength = fieldValue?.length ?? 0;

  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange) {
        onChange(event);
      }
    },
    [onChange],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      // Prevent leading and trailing spaces in email inputs
      if (type === 'email' && event.key === ' ') {
        event.preventDefault();
      }

      if (onKeyDown) {
        onKeyDown(event);
      }
    },
    [type, onKeyDown],
  );

  const handlePaste = useCallback(
    (event: React.ClipboardEvent<HTMLInputElement>) => {
      // Trim leading and trailing spaces in email inputs
      if (type === 'email') {
        const pastedText = event.clipboardData.getData('text');
        const trimmedText = pastedText.trim();
        if (pastedText !== trimmedText) {
          event.preventDefault();
          // NOTE: Using deprecated execCommand('insertText') because it's the only way to:
          // 1. Properly handle text insertion at cursor position
          // 2. Preserve undo/redo functionality (Ctrl+Z/Ctrl+Y)
          // 3. Trigger proper DOM events for React Hook Form integration
          // Alternative approaches (manual value setting) break undo/redo behavior.
          // This command is still widely supported across all modern browsers.
          document.execCommand('insertText', false, trimmedText);
        }
      }

      if (onPaste) {
        onPaste(event);
      }
    },
    [type, onPaste],
  );

  return (
    <InputGroup className={className}>
      {label ? (
        <Tooltip tooltip={tooltip} enabled={!!tooltip}>
          <div>
            <label
              htmlFor={inputId}
              className={clsx('laane:mb-1 block', labelClassName)}
            >
              {label}
            </label>
            {labelDescription ? (
              <div
                className={clsx(
                  'laane:contents kunde:text-neutral-100 kunde:text-2xs',
                )}
              >
                {labelDescription}
              </div>
            ) : null}
          </div>
        </Tooltip>
      ) : null}
      <div className={inputContainerClassName}>
        <BaseInput
          {...rest}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          className={inputClassName}
          id={inputId}
          ref={ref}
          type={type}
          aria-describedby={!!error && errorId ? errorId : undefined}
          placeholder={placeholder || (typeof label === 'string' ? label : '')}
          disabled={disabled}
          error={!!error}
          aria-errormessage={errorId}
          valid={valid}
        />
        {maxLength && maxLength > 0 ? (
          <div className="mt-2 flex justify-end">
            <span
              className={clsx('typ-ui-sm', {
                'text-neutral-100': currentLength < maxLength,
                'text-red-100': currentLength > maxLength,
              })}
              data-testid={`max-length-${rest.name}`}
            >
              {`${currentLength}/${maxLength}`}
            </span>
          </div>
        ) : null}
        {error ? (
          <div
            id={errorId}
            data-testid={`error-${rest.name}`}
            className={clsx(
              'typ-headline-2xs mt-2 flex items-start justify-start space-x-1 text-red-100',
              {
                hidden: hideError,
              },
            )}
          >
            <Icon type="alert-diamond" solid size="tiny" />
            <span className="flex-1">
              {typeof errorMessage === 'function'
                ? errorMessage(error)
                : error.message}
            </span>
          </div>
        ) : null}
      </div>
    </InputGroup>
  );
};

export default Input;
